import { useMemo } from "react";

import {
  convertShiftToSlotId,
  type ShiftSlotCoalesced,
  useGetShiftSlotsV2,
} from "../../../Shift/Open/useGetShiftsSlotV2";
import { useGetFriendsList } from "../../../WorkWithFriends/hooks/useGetFriendsList";
import { useGetOutboundFriendsRequests } from "../../../WorkWithFriends/hooks/useGetOutboundFriendsRequests";
import { type WorkerPublicProfile } from "../../../WorkWithFriends/hooks/useGetWorkerPublicProfile";

interface UseGetWorkedWithProfessionalsParams {
  shiftId: string;
  shiftStart: string;
  shiftEnd: string;
  workplaceId: string;
  workerId: string;
}

interface UseGetWorkedWithProfessionalsResult {
  filteredSlot?: ShiftSlotCoalesced;
  friendsMap: Map<string, WorkerPublicProfile>;
  outboundFriendRequestsSet: Set<string>;
}

export function useGetWorkedWithProfessionals(
  params: UseGetWorkedWithProfessionalsParams
): UseGetWorkedWithProfessionalsResult {
  const { shiftId, shiftStart, shiftEnd, workplaceId, workerId } = params;

  const shiftSlots = useGetShiftSlotsV2({
    shiftIds: [shiftId],
  });

  const { data: friendsList } = useGetFriendsList();
  const friendsMap = useMemo(
    () => new Map(friendsList?.data.map((friend) => [friend.id, friend])),
    [friendsList?.data]
  );

  const { data: outboundFriendRequests } = useGetOutboundFriendsRequests();
  const outboundFriendRequestsSet = useMemo(
    () => new Set(outboundFriendRequests?.data.map((worker) => worker.id)),
    [outboundFriendRequests?.data]
  );

  const filteredSlot = useMemo(() => {
    const slotId = convertShiftToSlotId({
      start: shiftStart,
      end: shiftEnd,
      facilityId: workplaceId,
    });
    const rawSlot = shiftSlots[slotId];
    const filteredAssignments =
      rawSlot?.shiftAssignments?.filter((a) => a.workerId !== workerId) ?? [];

    if (!rawSlot || filteredAssignments.length === 0) {
      return undefined;
    }

    return { ...rawSlot, shiftAssignments: filteredAssignments };
  }, [shiftSlots, shiftStart, shiftEnd, workplaceId, workerId]);

  return {
    filteredSlot,
    friendsMap,
    outboundFriendRequestsSet,
  };
}
